import { useState } from "react";
import { recoverMissingWords, type RecoveryOutcome } from "@app/btc-adapter";
import { apiGet, apiGetBlob } from "../lib/api";
import GraphView from "../components/graph/GraphView";
import type { RawGraph } from "../components/graph/types";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";

// The standard, publicly-published BIP-39 test vector used throughout the
// Bitcoin ecosystem's own test suites — not a real person's wallet. See
// note below the recovery demo for why we don't derive or show a real
// address from it.
const DEMO_TEMPLATE: (string | null)[] =
  "abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon ?"
    .split(" ")
    .map((w) => (w === "?" ? null : w));

export default function Demo() {
  const [recoveryOutcome, setRecoveryOutcome] = useState<RecoveryOutcome | null>(null);
  const [runningRecovery, setRunningRecovery] = useState(false);

  const [graph, setGraph] = useState<RawGraph | null>(null);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [reportFormat, setReportFormat] = useState<string | null>(null);
  const demoCandidate = recoveryOutcome?.candidates.find((c) => c.validity === "checksum-valid-unverified");

  function runRecoveryDemo() {
    setRunningRecovery(true);
    // A single blank word is a tiny search (2048 candidates at most) —
    // still async now that recoverMissingWords supports cancellation, but
    // fast enough that a worker isn't necessary for this demo.
    setTimeout(async () => {
      const outcome = await recoverMissingWords([...DEMO_TEMPLATE]);
      setRecoveryOutcome(outcome);
      setRunningRecovery(false);
    }, 300); // small delay purely so the "Searching…" state is visible
  }

  async function loadTraceDemo() {
    setLoadingGraph(true);
    try {
      const data = await apiGet<RawGraph>("/api/demo/graph");
      setGraph(data);
    } finally {
      setLoadingGraph(false);
    }
  }

  async function downloadDemoReport(format: "json" | "csv" | "pdf") {
    setReportFormat(format);
    try {
      const blob = await apiGetBlob(`/api/demo/report/${format}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `demo-investigation.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setReportFormat(null);
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-16">
      <h1 className="font-display text-2xl mb-1">Demo</h1>
      <p className="text-sm text-muted mb-10">
        Both demos below use fabricated or public test data — nothing here touches a real wallet,
        real funds, or a real person.
      </p>

      {/* --- Recovery demo --- */}
      <section className="mb-14">
        <h2 className="font-display text-lg mb-2">Wallet recovery</h2>
        <p className="text-sm text-muted mb-4">
          This runs the same recovery engine as the real /recover page, entirely in your browser.
          The mnemonic below is the standard public BIP-39 test vector (published in the Bitcoin
          specification itself) — not a real person's wallet. We deliberately don't derive or show
          an address from it here: this vector is so widely known that people have sent small real
          donations to its address over the years, so showing "the" address risks implying this demo
          is looking at a real, funded wallet. The point of this demo is the recovery mechanic, not a
          specific address.
        </p>
        <Card padding="md">
          <p className="font-mono text-xs break-all text-muted">
            {DEMO_TEMPLATE.map((w) => w ?? "?").join(" ")}
          </p>
          <Button onClick={runRecoveryDemo} disabled={runningRecovery} className="mt-4">
            {runningRecovery ? "Searching…" : "Find the missing word"}
          </Button>
          {recoveryOutcome && (
            <div className="mt-4">
              {demoCandidate ? (
                <>
                  <p className="text-xs text-signal-400">Checksum-valid candidate found</p>
                  <p className="mt-1 font-mono text-sm break-all">{demoCandidate.mnemonic}</p>
                </>
              ) : (
                <p className="text-xs text-red-400">No candidate found</p>
              )}
              <p className="mt-2 text-xs text-muted">
                In the real /recover flow, this is the point where you'd supply your own public
                address to confirm it's actually your wallet — a checksum-valid guess alone is never
                treated as "recovered."
              </p>
            </div>
          )}
        </Card>
      </section>

      {/* --- Trace demo --- */}
      <section className="mb-10">
        <h2 className="font-display text-lg mb-2">Blockchain investigation</h2>
        <p className="text-sm text-muted mb-4">
          A fabricated scenario: funds leave a wallet, move through two intermediate addresses, and
          reach an address attributed (in this demo's synthetic dataset only) to an example exchange.
        </p>
        <Card padding="md">
          {!graph ? (
            <Button onClick={loadTraceDemo} disabled={loadingGraph}>
              {loadingGraph ? "Loading…" : "Load demo investigation"}
            </Button>
          ) : (
            <GraphView initialGraph={graph} rootAddress={graph.nodes[0].address} network="bitcoin" disableExpand />
          )}

          {graph && (
            <div className="mt-4 flex flex-wrap gap-2">
              {(["json", "csv", "pdf"] as const).map((format) => (
                <Button
                  key={format}
                  variant="ghost"
                  size="sm"
                  onClick={() => downloadDemoReport(format)}
                  disabled={reportFormat === format}
                >
                  {reportFormat === format ? "Preparing…" : `Demo report (${format.toUpperCase()})`}
                </Button>
              ))}
            </div>
          )}
        </Card>
      </section>

      <p className="text-sm text-signal-400 border-t border-ink-700 pt-6">
        This is a demonstration using test data. No real wallet credentials were used.
      </p>
    </div>
  );
}
